/**
 * Left panel of the Medical Necessity screen.
 *
 * Renders the order's clinical document with the oasis {@link PdfRenderer}, and
 * the shared {@link OasisPdfToolbar} whose leftmost control is the document
 * index built from `document_page_mapping`. The built-in thumbnails side panel
 * is disabled in favour of that index.
 *
 * The oasis renderer exposes no rotate API, so page rotation is owned here: the
 * angle is kept in local state and applied to the rendered `.pdf-renderer__page`
 * elements via a scoped `<style>` rule (which also covers pages mounted later as
 * the user scrolls).
 *
 * Oasis text selection is enabled for copy and criterion proof capture. A
 * renderer-internal bridge also converts a completed text selection or marquee
 * region into a feedback draft.
 *
 * `worker` is off deliberately (same rationale as the Candid auth-letter
 * panel): in worker mode a failed wasm load posts a `wasmError` message the
 * host drops, so the viewer hangs on "Loading PDF…" with nothing logged. The
 * direct engine awaits the wasm, so a failure reaches `onError` instead.
 *
 * When the panel shrinks (divider drag or window resize), a manually zoomed
 * page may overflow the viewport. A `ResizeObserver` re-fits it to the new
 * width in that case; zooms that still fit are left untouched.
 * @module MedicalNecessityPdfPanel
 */
import {
  PdfRenderer,
  PdfRendererHandle,
  PdfZoomLevel,
} from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useEffect, useRef, useState } from "react";
import { LoaderMessage } from "../../../components/loaderMessage/loaderMessage";
import { OasisPdfToolbar } from "../../../components/oasisPdfToolbar/OasisPdfToolbar";
import { DocumentPageMapping } from "../../../data-model/medicalPaOrdersModel";
import { MedicalNecessityAnalytics } from "../analytics/useMedicalNecessityAnalytics";
import { usePdfNavigation } from "../context/PdfNavigationContext";
import { usePdfProofCapture } from "../context/PdfProofCaptureContext";
import { PdfProofCaptureBridge } from "./PdfProofCaptureBridge";
import { TableOfContentsButton, TableOfContentsTable } from "./TableOfContents";

export interface MedicalNecessityPdfPanelProps {
  pdfUrl: string | null | undefined;
  isLoading: boolean;
  error: string | null | undefined;
  /** Per-source-document page ranges used to build the document index. */
  documentPageMapping?: DocumentPageMapping[];
  /** Medical Necessity analytics facade for PDF tool events. */
  analytics: MedicalNecessityAnalytics;
  /** Panel width as a percentage of the analysis row (user-resizable). */
  widthPct: number;
  /** Order-level date shown in the Table of Contents when a document has no per-document date. */
  visitDate?: string;
}

/**
 * Renders the Medical Necessity PDF viewer panel.
 *
 * @input props - PDF URL/loading state, document mappings, and analytics
 * @return The left-panel PDF viewer
 */
const MedicalNecessityPdfPanel: React.FC<MedicalNecessityPdfPanelProps> = ({
  pdfUrl,
  isLoading,
  error,
  documentPageMapping,
  analytics,
  widthPct,
  visitDate,
}) => {
  const pdfRef = useRef<PdfRendererHandle>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [rotation, setRotation] = useState(0);
  const [documentId, setDocumentId] = useState<string | null>(null);
  /** True while the Table of Contents table replaces the PDF in the document area. */
  const [showTable, setShowTable] = useState(false);
  /** Measured toolbar height so the table overlay starts exactly below it. */
  const [toolbarHeight, setToolbarHeight] = useState(45);
  /** Last zoom level reported by the renderer (numeric = manual zoom). */
  const [zoomLevel, setZoomLevel] = useState<PdfZoomLevel>("automatic");
  /** Previous panel width in px, so only shrinking resizes re-fit the zoom. */
  const prevWidthRef = useRef(0);
  const { mode } = usePdfProofCapture();
  const { registerNavigate } = usePdfNavigation();

  const mappings = documentPageMapping ?? [];

  /** Advances page rotation by 90° (0 → 90 → 180 → 270 → 0). */
  const rotatePage = () => setRotation((prev) => (prev + 90) % 360);

  // Register the PDF's navigate callback so evidence DocChunk references can
  // deep-link it. The ref is read at click time (not captured here) so a
  // re-mounted renderer never leaves a stale handle behind; `documentId` in the
  // deps re-runs registration once the document has actually loaded.
  useEffect(() => {
    if (!pdfUrl) return undefined;
    registerNavigate((page) => pdfRef.current?.goToPage(page));
    return undefined;
  }, [pdfUrl, documentId, registerNavigate]);

  // Track the toolbar's rendered height so the Table of Contents overlay can
  // start exactly below it (the toolbar stays mounted while the table shows).
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;
    const slot = panel.querySelector(".pdf-renderer__toolbar-slot");
    if (!slot) return undefined;
    const measure = () => setToolbarHeight(slot.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(slot);
    return () => observer.disconnect();
  }, [pdfUrl, documentId]);

  // When the panel shrinks (divider drag or window resize), a manually zoomed
  // page can overflow the viewport. Re-fit it to the new width in that case;
  // zooms that still fit are left untouched, and fit-modes already track the
  // width on their own.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || !documentId) return undefined;
    const viewport = panel.querySelector<HTMLElement>(
      ".pdf-renderer__viewport",
    );
    if (!viewport) return undefined;
    const observer = new ResizeObserver(() => {
      const width = viewport.clientWidth;
      const prevWidth = prevWidthRef.current;
      prevWidthRef.current = width;
      if (prevWidth === 0 || width >= prevWidth) return;
      if (typeof zoomLevel !== "number") return;
      const page = viewport.querySelector<HTMLElement>(".pdf-renderer__page");
      if (!page || page.clientWidth <= width) return;
      pdfRef.current?.setZoom("fit-width");
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [pdfUrl, documentId, zoomLevel]);

  return (
    <div
      ref={panelRef}
      className={`medical-necessity-analysis--pdf relative flex h-full flex-col overflow-hidden border-r border-primaryGray-15 ${
        mode !== "idle" ? "[&_.pdf-renderer__viewport]:cursor-crosshair" : ""
      }`}
      style={{ width: `${widthPct}%` }}
    >
      {rotation !== 0 && (
        <style>
          {`.medical-necessity-analysis--pdf .pdf-renderer__page {` +
            ` transform: rotate(${rotation}deg); transform-origin: center center; }`}
        </style>
      )}
      {isLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <LoaderMessage message="Loading document..." />
        </div>
      ) : error ? (
        <div className="flex h-full w-full items-center justify-center text-sm text-red-500">
          {error}
        </div>
      ) : pdfUrl ? (
        <PdfRenderer
          ref={pdfRef}
          src={pdfUrl}
          onDocumentLoad={({ documentId: loadedDocumentId }) =>
            setDocumentId(loadedDocumentId)
          }
          onZoomChange={({ zoomLevel: level }) => setZoomLevel(level)}
          wasmUrl="/pdfium.wasm"
          worker={false}
          className="h-full w-full !rounded-none !border-0"
          height="100%"
          width="100%"
          features={{
            zoom: true,
            search: true,
            textSelection: true,
            thumbnails: false,
            pageNavigation: true,
          }}
          toolbar={(ctx) => (
            <>
              <PdfProofCaptureBridge documentId={documentId} />
              <OasisPdfToolbar
                ctx={ctx}
                rotation={rotation}
                onRotate={rotatePage}
                onSearchSubmitted={analytics.trackSearchSubmitted}
                leadingContent={
                  mappings.length > 0 ? (
                    <TableOfContentsButton
                      showTable={showTable}
                      onToggle={() => setShowTable((s) => !s)}
                      onListOpen={() => analytics.trackDocContentsOpened()}
                    />
                  ) : undefined
                }
              />
            </>
          )}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center p-4 text-sm text-primaryGray-10">
          No clinical document available for this order.
        </div>
      )}
      {showTable && mappings.length > 0 && !isLoading && !error && pdfUrl && (
        <div
          className="absolute inset-x-0 bottom-0 z-10 bg-white p-4"
          style={{ top: toolbarHeight }}
        >
          <TableOfContentsTable
            mappings={mappings}
            visitDate={visitDate}
            onSelectPage={(page) => {
              pdfRef.current?.goToPage(page);
              setShowTable(false);
            }}
            onReportSelected={(mapping) => {
              const reportId = `${mapping.doc_type ?? "doc"}:${
                mapping.start_page ?? 0
              }`;
              const reportTitle = mapping.doc_name || "Untitled document";
              analytics.trackPdfReportSelected(reportId, reportTitle);
            }}
          />
        </div>
      )}
      {mode !== "idle" && (
        <div className="pointer-events-none absolute bottom-4 left-1/2 z-20 -translate-x-1/2 rounded bg-[#0F0F0F] px-3 py-2 text-[11px] font-semibold text-white shadow">
          {mode === "text"
            ? "Drag across text to add a reference"
            : "Drag a box around the PDF area to capture"}
        </div>
      )}
    </div>
  );
};

export default MedicalNecessityPdfPanel;
export { MedicalNecessityPdfPanel };
